// src/components/streak/StreakBadge.tsx
// Reusable streak pill — used by Navbar and HomePage hero.
//
// Visibility rules:
//   • Anonymous / not logged in → hidden entirely.
//   • Logged in but NOT premium → shows "0" with a lock icon + "Go Premium" upsell.
//   • Premium + streak = 0 → softer amber flame + "0", tooltip "Complete a lesson to start your streak!".
//   • Premium + streak > 0 → full amber flame + count.

import { IconFlame } from '../icons/Icons';
import { useStreak } from '../../hooks/useStreak';
import { formatStreak } from '../../services/streakService';
import { useAuthStore } from '../../stores/useAuthStore';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useT } from '../../data/translations';

interface Props {
  size?: 'sm' | 'md';
  /** No longer used — premium users always see the badge regardless of streak value. */
  showZero?: boolean;
}

export function StreakBadge({ size = 'sm' }: Props) {
  const streak  = useStreak();
  const user    = useAuthStore(s => s.user);
  const profile = useAuthStore(s => s.profile);
  const C       = useThemePalette();
  const t       = useT();

  // Hide for anonymous / logged-out users entirely
  if (!user || user.isAnonymous) return null;

  const isSubscribed =
    profile?.subscription?.status === 'active' ||
    profile?.hasFullAccess === true;

  const isMd = size === 'md';

  // ── Non-premium: locked pill with upsell ────────────────────────────────────
  if (!isSubscribed) {
    return (
      <div
        title="Premium feature — upgrade to track your streak"
        style={{
          display:      'inline-flex',
          alignItems:   'center',
          gap:          isMd ? '6px' : '5px',
          padding:      isMd ? '9px 16px' : '5px 12px',
          borderRadius: '999px',
          background:   'rgba(139,156,196,0.07)',
          border:       `1px solid ${C.border}`,
          fontSize:     isMd ? '0.88rem' : '0.72rem',
          fontWeight:   700,
          color:        C.muted,
          whiteSpace:   'nowrap',
          flexShrink:   0,
          cursor:       'default',
          userSelect:   'none',
        }}
      >
        <svg
          width={isMd ? 13 : 10}
          height={isMd ? 13 : 10}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        0
        <span style={{
          fontSize:  isMd ? '0.72rem' : '0.6rem',
          fontWeight: 500,
          opacity:   0.65,
        }}>
          · {t('STREAK_UPSELL')}
        </span>
      </div>
    );
  }

  // ── Premium: always shown — softer at 0, full amber when active ────────────
  const display    = streak > 0 ? formatStreak(streak) : '0';
  const flameColor = streak > 0 ? '#fbbf24' : 'rgba(251,191,36,0.45)';
  const tooltip    = streak === 0
    ? 'Complete a lesson to start your streak!'
    : streak === 1
      ? '1 day streak — keep it going tomorrow!'
      : `${display} day streak — train daily to extend it.`;

  return (
    <div
      title={tooltip}
      aria-label={tooltip}
      style={{
        display:       'inline-flex',
        alignItems:    'center',
        gap:           isMd ? '6px' : '5px',
        padding:       isMd ? '9px 16px' : '5px 12px',
        borderRadius:  '999px',
        background:    streak > 0 ? 'rgba(251,191,36,0.10)' : 'rgba(251,191,36,0.04)',
        border:        streak > 0 ? '1px solid rgba(251,191,36,0.30)' : '1px solid rgba(251,191,36,0.12)',
        fontSize:      isMd ? '0.9rem' : '0.72rem',
        fontWeight:    isMd ? 800 : 700,
        letterSpacing: isMd ? 'normal' : '0.04em',
        color:         flameColor,
        whiteSpace:    'nowrap',
        flexShrink:    0,
        cursor:        'default',
        userSelect:    'none',
      }}
    >
      <IconFlame size={isMd ? 15 : 12} color={flameColor} />
      {display}
    </div>
  );
}
